import type { ApiErrorBody } from '@obralink/shared';

/**
 * Cliente HTTP de la API.
 *
 * Dos responsabilidades más allá de hacer peticiones:
 *
 *  1. TRADUCIR ERRORES una sola vez. Toda respuesta de error de la API tiene
 *     la misma forma (plan.md §28.5), así que se convierte aquí en `ApiError`
 *     y el resto de la aplicación decide sobre `error.code`, que es estable.
 *
 *  2. RENOVAR LA SESIÓN de forma transparente. El token de acceso dura 15
 *     minutos; si caduca a mitad de una sesión de trabajo, el usuario no debe
 *     enterarse. Ver `refreshOnce` más abajo.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    override readonly message: string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const BASE_URL = '/api/v1';

/**
 * El token vive SÓLO en memoria, nunca en localStorage ni sessionStorage:
 * cualquier XSS podría leerlo de ahí. Al recargar la página se pierde, y se
 * recupera con la cookie de refresco, que JavaScript no puede leer
 * (plan.md §29.1).
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

export function setSessionLostHandler(handler: (() => void) | null) {
  onSessionLost = handler;
}

/**
 * Renovación con deduplicación.
 *
 * Si cinco peticiones caducan a la vez —cosa normal al volver a una pestaña
 * abierta— NO se pueden lanzar cinco renovaciones: el refresh token rota en
 * cada uso, así que la segunda presentaría uno ya consumido, el servidor lo
 * interpretaría como robo y revocaría la sesión entera. Todas comparten la
 * misma promesa.
 */
/** Ver `bootstrapSession` para por qué son tres estados y no dos. */
export type ResultadoDeSesion = 'ok' | 'rechazado' | 'sin-red';

let refreshPromise: Promise<ResultadoDeSesion> | null = null;

/**
 * Renovación inicial al cargar la página. Se memoriza para SIEMPRE (mientras
 * viva la pestaña), no sólo mientras está en vuelo.
 *
 * Motivo: React en modo estricto ejecuta los efectos DOS veces en desarrollo.
 * Sin esta memorización, el arranque presentaba la misma cookie dos veces, el
 * servidor lo interpretaba —correctamente— como reutilización de un refresh
 * token y revocaba la sesión entera. La aplicación se auto-expulsaba.
 *
 * No es un problema exclusivo del modo estricto: dos pestañas abriéndose a la
 * vez producen lo mismo. La memorización por pestaña resuelve el primer caso;
 * el segundo lo absorbe el propio servidor, porque cada pestaña tiene su
 * propia cadena.
 */
let bootstrapPromise: Promise<ResultadoDeSesion> | null = null;

/**
 * Recupera la sesión al arrancar.
 *
 * DEVUELVE TRES ESTADOS Y NO DOS, y la diferencia importa mucho: «el servidor
 * dice que no» y «no se ha llegado al servidor» exigen respuestas opuestas. Con
 * un booleano las dos acababan en lo mismo —cerrar la sesión y mandar al
 * acceso—, así que recargar la aplicación sin cobertura te expulsaba. Justo en
 * el caso que la PWA viene a resolver: el encargado en un sótano recargando sin
 * querer, con incidencias pendientes en la cola.
 */
export function bootstrapSession(): Promise<ResultadoDeSesion> {
  bootstrapPromise ??= refreshOnce();
  return bootstrapPromise;
}

/**
 * Renueva la sesión UNA vez, compartiendo la petición entre todo el que la
 * pida a la vez. Se exporta para que las descargas de ficheros —que no pasan
 * por `api` porque la respuesta no es JSON— puedan reintentar igual que las
 * demás peticiones en lugar de fallar con un «no se ha podido generar».
 */
export async function refreshOnce(): Promise<ResultadoDeSesion> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      // Una respuesta, aunque sea mala, significa que SÍ se ha llegado: es un
      // rechazo de verdad y hay que cerrar la sesión.
      if (!response.ok) return 'rechazado';
      const payload = (await response.json()) as { data: { accessToken: string } };
      accessToken = payload.data.accessToken;
      return 'ok';
    } catch {
      // `fetch` sólo lanza cuando no ha habido respuesta: sin red, DNS caído,
      // servidor inalcanzable. Nunca por un código de estado.
      return 'sin-red';
    } finally {
      // Se libera en el siguiente ciclo para que las peticiones que llegaron
      // mientras tanto reutilicen el resultado y no disparen otra renovación.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

async function request<T>(path: string, init: RequestInit, isRetry = false): Promise<T> {
  // ── SIN TOKEN, SE INTENTA RECUPERAR LA SESIÓN ANTES DE PEDIR NADA ────────
  //
  // Pasa después de arrancar sin cobertura: la aplicación se ha pintado con la
  // última ficha conocida pero NO tiene token, porque no se pudo renovar. En
  // cuanto vuelve la red, la primera petición saldría sin autorización, el
  // servidor respondería 401 y —lo peor— la cola de incidencias pendientes daría
  // ese 401 por definitivo y quemaría lo que tenía guardado.
  //
  // Se pide la renovación aquí, que es donde se sabe que hace falta.
  if (!accessToken && !path.startsWith('/auth/')) {
    await refreshOnce();
  }

  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    const code = body?.error?.code ?? 'INTERNAL_ERROR';

    // Token caducado: se renueva y se reintenta UNA vez. Sin el guardia
    // `isRetry` esto sería un bucle infinito cuando la sesión ya no es válida.
    const isExpired = response.status === 401 && code === 'AUTH_TOKEN_EXPIRED';
    const isRefreshCall = path.startsWith('/auth/refresh');

    if (isExpired && !isRetry && !isRefreshCall) {
      const renovado = await refreshOnce();
      if (renovado === 'ok') return request<T>(path, init, true);
      // Sólo se cierra la sesión si el servidor la ha RECHAZADO. Sin red no se
      // cierra nada: la sesión puede seguir siendo válida y lo único que pasa
      // es que no se llega a preguntarlo.
      if (renovado === 'rechazado') onSessionLost?.();
    }

    if (response.status === 401 && !isRefreshCall) onSessionLost?.();

    throw new ApiError(
      code,
      body?.error?.message ?? 'Error de comunicación con el servidor.',
      response.status,
      body?.error?.details,
      body?.error?.requestId,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, {}),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  // PUT y no PATCH cuando se manda el estado COMPLETO de un recurso —la lista
  // entera de etiquetas de un archivo, por ejemplo— y no un cambio parcial.
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/** Envoltorio de respuesta de la API: `{ data, meta }`. */
export interface ApiEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}
